/**
 * FastTrackModal — recruiter-facing modal to convert an accepted
 * DaddyTalk chat into a live pipeline candidate on a chosen job, with
 * an optional interview slot in the same tap.
 *
 * Backed by POST /api/daddytalk/threads/{id}/fast-track.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Rocket,
    Briefcase,
    CalendarBlank,
    Check,
    ArrowRight,
} from "@phosphor-icons/react";
import BottomSheet from "@/components/BottomSheet";

export default function FastTrackModal({ open, threadId, candidateName, onClose, onDone }) {
    const [jobs, setJobs] = useState([]);
    const [jobId, setJobId] = useState("");
    const [scheduleInterview, setScheduleInterview] = useState(false);
    const [interviewAt, setInterviewAt] = useState("");
    const [mode, setMode] = useState("video");
    const [duration, setDuration] = useState(30);
    const [saving, setSaving] = useState(false);
    const [loadingJobs, setLoadingJobs] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoadingJobs(true);
        (async () => {
            try {
                const res = await api.get("/jobs?status=Open&limit=100");
                const items = res?.data?.items || res?.data || [];
                setJobs(items);
                if (items.length && !jobId) setJobId(items[0].id);
            } catch (err) {
                toast.error(err?.response?.data?.detail || "Couldn't load open jobs");
            } finally { setLoadingJobs(false); }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const minDatetime = useMemo(() => {
        // Bound the datetime input at ~15 minutes from now so recruiters
        // can't schedule interviews in the past.
        const d = new Date(Date.now() + 15 * 60 * 1000);
        d.setSeconds(0, 0);
        return d.toISOString().slice(0, 16);
    }, []);

    const submit = async () => {
        if (!jobId) { toast.error("Pick a job first."); return; }
        setSaving(true);
        try {
            const body = { job_id: jobId };
            if (scheduleInterview && interviewAt) {
                // Convert the browser-local datetime-local string to a
                // proper UTC ISO string.
                body.interview_at = new Date(interviewAt).toISOString();
                body.mode = mode;
                body.duration_min = duration;
            }
            const res = await api.post(`/daddytalk/threads/${threadId}/fast-track`, body);
            toast.success(
                res?.data?.interview_id
                    ? "Fast-tracked — candidate added and interview scheduled ✨"
                    : "Fast-tracked — candidate added to the pipeline ✨",
            );
            onDone?.(res?.data);
            onClose?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Fast-track failed");
        } finally { setSaving(false); }
    };

    return (
        <BottomSheet
            open={open}
            onClose={onClose}
            testId="daddytalk-fasttrack-modal"
            title={
                <span className="inline-flex items-center gap-1.5">
                    <Rocket size={16} weight="bold" className="text-cobalt" />
                    Fast-track {candidateName || "candidate"}
                </span>
            }
            footer={
                <>
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-[11px] font-black uppercase tracking-wider text-ink/60 hover:text-ink"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={saving || !jobId || (scheduleInterview && !interviewAt)}
                        data-testid="daddytalk-fasttrack-submit"
                        className="btn-gradient inline-flex items-center gap-2 px-5 py-2 text-[11px] font-black uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {saving ? "Fast-tracking…" : (
                            <>
                                <Rocket size={12} weight="bold" />
                                Fast-track
                                <ArrowRight size={11} weight="bold" />
                            </>
                        )}
                    </button>
                </>
            }
        >
            <div className="px-5 pt-3 pb-1">
                <div className="flex items-center gap-3 pb-3 border-b border-ink/10">
                    <img
                        src="/mascot/daddytalk/avatar_celebrating.png"
                        alt=""
                        className="w-10 h-10 rounded-full border-2 border-cream shrink-0"
                    />
                    <p className="text-xs text-ink/60 leading-snug">
                        Skip the queue — add them directly to the Kanban + schedule interview in one tap.
                    </p>
                </div>
            </div>
            <div className="p-5 space-y-4">
                    {/* Job picker */}
                    <label className="block">
                        <span className="text-[10px] font-black uppercase tracking-wider text-ink/60 mb-1 flex items-center gap-1">
                            <Briefcase size={12} weight="bold" />
                            Add to job
                        </span>
                        {loadingJobs ? (
                            <div className="text-xs text-ink/50 py-2">Loading open jobs…</div>
                        ) : jobs.length === 0 ? (
                            <div className="text-xs text-ink/50 py-2 px-3 border border-ink/10 bg-cream">
                                No open jobs in your workspace. Post one first.
                            </div>
                        ) : (
                            <select
                                value={jobId}
                                onChange={(e) => setJobId(e.target.value)}
                                data-testid="daddytalk-fasttrack-job"
                                className="w-full px-3 py-2 border border-ink/20 text-sm focus:outline-none focus:border-cobalt bg-white"
                            >
                                {jobs.map((j) => (
                                    <option key={j.id} value={j.id}>
                                        {j.title}{j.location ? ` · ${j.location}` : ""}
                                    </option>
                                ))}
                            </select>
                        )}
                    </label>

                    {/* Interview toggle */}
                    <label className="flex items-start gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={scheduleInterview}
                            onChange={(e) => setScheduleInterview(e.target.checked)}
                            data-testid="daddytalk-fasttrack-schedule-toggle"
                            className="mt-0.5"
                        />
                        <span>
                            <span className="text-sm font-bold flex items-center gap-1.5">
                                <CalendarBlank size={13} weight="bold" className="text-cobalt" />
                                Also schedule an interview
                            </span>
                            <p className="text-[11px] text-ink/50 mt-0.5">
                                Lands the candidate at "Interview Scheduled" instead of "Applied".
                            </p>
                        </span>
                    </label>

                    {scheduleInterview && (
                        <div className="space-y-3 border-l-2 border-cobalt/30 pl-3">
                            <label className="block">
                                <span className="text-[10px] font-black uppercase tracking-wider text-ink/60 mb-1 block">
                                    When
                                </span>
                                <input
                                    type="datetime-local"
                                    min={minDatetime}
                                    value={interviewAt}
                                    onChange={(e) => setInterviewAt(e.target.value)}
                                    data-testid="daddytalk-fasttrack-when"
                                    className="w-full px-3 py-2 border border-ink/20 text-sm focus:outline-none focus:border-cobalt"
                                />
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                                <label className="block">
                                    <span className="text-[10px] font-black uppercase tracking-wider text-ink/60 mb-1 block">
                                        Mode
                                    </span>
                                    <select
                                        value={mode}
                                        onChange={(e) => setMode(e.target.value)}
                                        data-testid="daddytalk-fasttrack-mode"
                                        className="w-full px-3 py-2 border border-ink/20 text-sm focus:outline-none focus:border-cobalt bg-white"
                                    >
                                        <option value="video">Video call</option>
                                        <option value="phone">Phone</option>
                                        <option value="in_person">In person</option>
                                    </select>
                                </label>
                                <label className="block">
                                    <span className="text-[10px] font-black uppercase tracking-wider text-ink/60 mb-1 block">
                                        Duration (min)
                                    </span>
                                    <input
                                        type="number"
                                        min={15}
                                        max={240}
                                        step={15}
                                        value={duration}
                                        onChange={(e) => setDuration(Number(e.target.value))}
                                        data-testid="daddytalk-fasttrack-duration"
                                        className="w-full px-3 py-2 border border-ink/20 text-sm focus:outline-none focus:border-cobalt"
                                    />
                                </label>
                            </div>
                        </div>
                    )}
                </div>
        </BottomSheet>
    );
}
